import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { adminApi, errorMessage } from "../../lib/api";

const rs = (n) => `₹${Number(n).toFixed(2)}`;

const useCountdown = (until) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!until) return undefined;
    const tick = () => setLeft(Math.max(0, Math.round((new Date(until) - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [until]);
  return left;
};

export const UpiPay = ({ open, onClose, note, path, body = {}, onDone, testId = "upi-pay" }) => {
  const [upi, setUpi] = useState(null);
  const [req, setReq] = useState(null);
  const left = useCountdown(req?.expires_at);
  const started = useRef(false);
  const start = useCallback(() => {
    setReq(null);
    started.current = true;
    adminApi.post(path, body).then((r) => setReq(r.data)).catch((e) => { toast.error(errorMessage(e)); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  useEffect(() => {
    if (open && !started.current) {
      adminApi.get("/payments/upi").then((r) => setUpi(r.data)).catch(() => {});
      start();
    }
  }, [open, start]);
  useEffect(() => {
    if (!req || !["pending", "expired"].includes(req.status)) return undefined;
    const t = setInterval(() => {
      adminApi.get(`/payments/requests/${req.id}`).then((r) => {
        setReq(r.data);
        if (r.data.status === "approved") { toast.success("Payment received. Approved automatically."); setTimeout(onDone, 1200); }
      }).catch(() => {});
    }, req.status === "pending" ? 3000 : 8000);
    return () => clearInterval(t);
  }, [req, onDone]);
  const link = upi && req && `upi://pay?pa=${upi.upi_id}&pn=${encodeURIComponent(upi.payee_name)}&am=${req.pay_amount.toFixed(2)}&cu=INR&tn=${encodeURIComponent(note)}`;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid={`${testId}-dialog`} className="notranslate rounded-none border-white/10 bg-[#0F172A] text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white">Pay by UPI</DialogTitle>
          <DialogDescription className="text-slate-400">Pay the exact amount shown (including paise). It is approved automatically, no UTR needed.</DialogDescription>
        </DialogHeader>
        {!req && <Loader2 className="mx-auto h-6 w-6 animate-spin text-orange-400" />}
        {req && req.status === "pending" && link && <PayBox upi={upi} req={req} link={link} left={left} testId={testId} />}
        {req?.status === "approved" && <p data-testid={`${testId}-approved`} className="flex items-center gap-2 text-emerald-300"><CheckCircle2 className="h-5 w-5" />Payment of {rs(req.pay_amount)} received.</p>}
        {req?.status === "expired" && (
          <div data-testid={`${testId}-expired`} className="space-y-3 text-sm">
            <p className="text-amber-300">Time over. If you already paid {rs(req.pay_amount)}, it will still be matched automatically or approved by our team.</p>
            <button onClick={start} data-testid={`${testId}-retry`} className="bg-orange-500 px-4 py-2 font-semibold text-white">Start again</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PayBox = ({ upi, req, link, left, testId }) => (
  <div className="flex flex-wrap items-center gap-5">
    <div className="bg-white p-2"><QRCodeSVG value={link} size={150} data-testid={`${testId}-qr`} /></div>
    <div className="space-y-2 text-sm">
      <p>UPI ID: <span data-testid={`${testId}-upi-id`} className="font-mono text-orange-300">{upi.upi_id}</span></p>
      <p>Pay exactly: <span data-testid={`${testId}-amount`} className="font-mono text-2xl font-bold text-white">{rs(req.pay_amount)}</span></p>
      <p data-testid={`${testId}-timer`} className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" />Waiting for payment · {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</p>
      <a href={link} data-testid={`${testId}-open-app`} className="inline-block bg-orange-500 px-4 py-2 font-semibold text-white sm:hidden">Open UPI app</a>
    </div>
  </div>
);
